import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import { Box, Button, LinearProgress, Typography, useTheme } from '@mui/material'
import ResourceReportDialog from './ResourceReportDialog'
import InstanceSelectionDialog from './InstanceSelectionDialog'
import PartSelectorBar from './PartSelectorBar'
import GridViewIcon from '@mui/icons-material/GridView'
import MosSmartTable from '../common/MosSmartTable'
import CircleIcon from '@mui/icons-material/Circle'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import DefaultBeforeSelectText from '../common/DefaultBeforeSelectText'
import useMosInfo from '../common/provider/useMosInfo'
import useConsumptionMode from '../common/hooks/useConsumptionMode'
import NumericKeypad from '../common/NumericKeypad'
import AlertUI from '../../../generic/AlertUI'
import { getJobGenerateComponents } from '../common/reportOutcome'
import { allowsDecimalEntry, computeMaxQuantity } from './componentQuantityLimits'

const isRowComplete = (row) => {
  const pending = row.pending ?? ((row.quantityFirstUnit ?? 0) - (row.reportedQuantity ?? 0))
  return pending <= 0
}

// Sample reportable parts shown in the widget picker preview (no job/device selected there).
const PREVIEW_PARTS = [
  { id: 1, code: 'PART-001', name: 'Sample part A', resourceConsumptionStatus: 'COMPLETE' },
  { id: 2, code: 'PART-002', name: 'Sample part B', resourceConsumptionStatus: 'PARTIAL' }
]

// The reportable-parts boxes are polled so they never drift from the DB (another operator or the
// Active Jobs widget on the same screen can report a part and change what is still reportable).
const REPORTABLE_PARTS_POLL_MS = 2000

export const ResourceListWidget = ({ device, preview }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [dialogInfo, setDialogInfo] = useState({ open: false, item: {} })
  const [reportableParts, setReportableParts] = useState([])
  const [selectedPartId, setSelectedPartId] = useState(0)
  // Per-part accumulation: the operator sets consumption for one active part at a time, switches
  // parts (each part remembers its own quantities), and nothing is posted until Confirm — which
  // sends every touched part in a single grouped body [{ partId, materials: [...] }].
  const [qtyByPartId, setQtyByPartId] = useState({})
  const [unitByPartId, setUnitByPartId] = useState({})
  const [refreshKey, setRefreshKey] = useState(0)
  const [keypadInfo, setKeypadInfo] = useState({ open: false, id: 0, max: 0, val: 0, fractionable: false })
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState({ total: 0, done: 0, pending: 0, percentage: 0, allDone: false })
  const [instanceSelectionDialog, setInstanceSelectionDialog] = useState({ open: false, referenceId: 0, referenceCode: '', pending: 0, available: 0 })
  const flagsByRefIdRef = useRef({})
  // `activeJobsProgress` changes whenever the Active Jobs widget reports (its progress is refreshed
  // right after a report) — used here as the "a report just happened" signal to refresh the boxes.
  const { jobReference, fabricationOrder, node, activeJobsProgress, activeJobs } = useMosInfo()
  // Story 79 (S41/RN3): the technology flag travels per job in the device active-context, read with
  // the very same pattern as autoReportResources/autoReportParts (absent job or flag -> false).
  const generateComponents = useMemo(
    () => getJobGenerateComponents(activeJobs, fabricationOrder, jobReference),
    [activeJobs, fabricationOrder, jobReference]
  )
  // #39: the consumption mode is server-persisted per machine (shared via MosInfoProvider). This
  // widget only reads it to drive the SELECCION two-step reporting flow (S33) — the selector that
  // changes it lives in the Machine Storage widget (MachineMaterialWidget).
  const { mode } = useConsumptionMode(device)
  const [AlertElement, showAlert] = AlertUI()

  // The table reads/writes only the active part's quantities and unit choices; every other part's
  // pending consumption stays parked in qtyByPartId/unitByPartId until Confirm.
  const activeQtyByRefId = useMemo(() => qtyByPartId[selectedPartId] ?? {}, [qtyByPartId, selectedPartId])
  const activeUnitByRefId = useMemo(() => unitByPartId[selectedPartId] ?? {}, [unitByPartId, selectedPartId])

  const partsWithQty = useMemo(() => {
    const flags = {}
    Object.entries(qtyByPartId).forEach(([partId, refMap]) => {
      if (Object.values(refMap).some((quantity) => quantity > 0)) flags[partId] = true
    })
    return flags
  }, [qtyByPartId])

  // The boxes at the top are the parts of the active job at this node (same rule as the report flow,
  // via the shared reportableParts endpoint). `includeReported` asks for the parts already reported
  // at the job too: the operator must still see them and may keep mounting their material after
  // reporting — without it a fully reported job shows an empty widget. To keep the DB and what the
  // operator sees in sync, they are polled every 2s and re-fetched immediately whenever the Active
  // Jobs widget reports (activeJobsProgress changes) or after our own confirm (refreshKey).
  useEffect(() => {
    if (preview) {
      setReportableParts(PREVIEW_PARTS)
      setSelectedPartId((prev) => prev || PREVIEW_PARTS[0].id)
      return
    }
    if (!fabricationOrder?.id || !jobReference?.id) {
      setReportableParts([])
      setSelectedPartId(0)
      return
    }
    let cancelled = false
    const loadReportableParts = async () => {
      try {
        const result = await apiGet(
          'mos/fabricationOrderReportQuantity/reportableParts/' + fabricationOrder.id + '/' + jobReference.id,
          { includeReported: true }
        )
        if (cancelled) return
        const list = Array.isArray(result) ? result : []
        setReportableParts(list)
        // Auto-select the first part on first load (and whenever the current selection is gone, e.g.
        // the part was cancelled) so the per-part panel is never left empty; keep the operator's
        // current selection while it is still listed. The endpoint returns the parts still pending
        // first, so this keeps landing on pending work while there is any.
        setSelectedPartId((prev) => {
          if (prev && list.some((part) => part.id === prev)) return prev
          return list.length > 0 ? list[0].id : 0
        })
      } catch (error) {
        // Transient poll error: keep the last known list rather than blanking the panel.
      }
    }
    loadReportableParts()
    const intervalId = setInterval(loadReportableParts, REPORTABLE_PARTS_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [preview, fabricationOrder?.id, jobReference?.id, refreshKey, activeJobsProgress])

  const toNumber = (value) => {
    const num = Number(value)
    return Number.isFinite(num) ? Math.round(num * 100) / 100 : 0
  }

  const isSecondUnitSelectable = (factor) => Number.isFinite(factor) && factor > 0

  const dataFech = useCallback(async () => {
    if (preview) {
      return [
        { resource: { id: 1, name: 'RW_001', code: 'CODE_001', revision: 'REV_1' }, quantityFirstUnit: 3, reportedQuantity: 0, available: 5 },
        { resource: { id: 2, name: 'RW_002', code: 'CODE_002', revision: 'REV_2' }, quantityFirstUnit: 3, reportedQuantity: 3, available: 1 }
      ]
    }

    // The materials panel is now per part: required/mounted are the figures of the SELECTED part at
    // this node (endpoint 2, byPartAndNodeInstance), not the whole-OF aggregate. Without a selected
    // part there is nothing per-part to show, so the table stays empty until the operator picks one.
    if (!selectedPartId || !node?.id) {
      return []
    }

    // `deviceId` lets the backend answer `available` with the machine stock when the machine has a
    // Machine Storage; `available` is the warehouse/machine stock and does NOT depend on the part.
    const params = device?.id ? { deviceId: device.id } : ''
    const serverItems = await apiGet('partWorkflowHistory/rawMaterial/byPartAndNodeInstance/' + selectedPartId + '/' + node.id, params)
    const rows = (Array.isArray(serverItems) ? serverItems : []).map((serverItem) => {
      const mounted = toNumber(serverItem?.mounted)
      const available = toNumber(serverItem?.available)
      const availableSecondUnit = toNumber(serverItem?.availableSecondUnit)
      const quantity = serverItem?.required
      return {
        resource: {
          id: serverItem.resourceId,
          name: serverItem.resourceName,
          code: serverItem.resourceCode,
          revision: serverItem.revision,
          fractionable: serverItem.fractionable,
          unitary: serverItem.unitary
        },
        quantityFirstUnit: quantity,
        reportedQuantity: mounted,
        available,
        availableSecondUnit,
        conversionFactor: serverItem?.conversionFactor,
        pending: Math.max(0, toNumber(quantity - mounted))
      }
    })
    flagsByRefIdRef.current = rows.reduce((acc, row) => {
      acc[row.resource.id] = {
        fractionable: row.resource.fractionable,
        unitary: row.resource.unitary,
        available: row.available,
        availableSecondUnit: row.availableSecondUnit,
        conversionFactor: row.conversionFactor
      }
      return acc
    }, {})
    return rows
  }, [preview, selectedPartId, node?.id, device?.id])

  const dataFetchWithProgress = useCallback(async (requestPagination) => {
    const result = await dataFech(requestPagination)
    const list = Array.isArray(result) ? result : []
    const total = list.length
    const done = list.filter(isRowComplete).length
    const pending = total - done
    const percentage = total > 0 ? Math.round((done / total) * 100) : 0
    const next = { total, done, pending, percentage, allDone: total > 0 && pending === 0 }
    // Dedupe: keep same reference (no re-render) when counts are unchanged
    setProgress(prev => (prev.total === next.total && prev.done === next.done ? prev : next))
    return result
  }, [dataFech])

  const getColorForRow = useCallback((row) => {
    return isRowComplete(row) ? theme.palette.success.dark : theme.palette.warning.dark
  }, [theme])

  const incQty = useCallback((id, max) => {
    if (!selectedPartId) return
    setQtyByPartId(prev => {
      const inner = prev[selectedPartId] ?? {}
      const next = Math.min((inner[id] ?? 0) + 1, max)
      return { ...prev, [selectedPartId]: { ...inner, [id]: next } }
    })
  }, [selectedPartId])

  const decQty = useCallback((id) => {
    if (!selectedPartId) return
    setQtyByPartId(prev => {
      const inner = prev[selectedPartId] ?? {}
      const next = Math.max((inner[id] ?? 0) - 1, 0)
      return { ...prev, [selectedPartId]: { ...inner, [id]: next } }
    })
  }, [selectedPartId])

  // S21 step 1: choosing a reference in SELECCION mode opens the instance-selection modal (step 2)
  // for that single reference.
  const openInstanceSelectionDialog = useCallback((row) => {
    // `pending` is what is still required for this reference at the active part — it caps the
    // instance selection so the operator cannot pick more than needed. `available` is the machine
    // stock for the reference: the dialog needs the authoritative total to decide whether the
    // "create required material" action applies, and its instances table is paginated (S32).
    setInstanceSelectionDialog({ open: true, referenceId: row.resource.id, referenceCode: row.resource.code, pending: row.pending ?? 0, available: row.available ?? 0 })
  }, [])

  const closeInstanceSelectionDialog = useCallback(() => {
    setInstanceSelectionDialog(prev => ({ ...prev, open: false }))
  }, [])

  const handleInstanceSelectionSuccess = useCallback(() => {
    closeInstanceSelectionDialog()
    setRefreshKey(v => v + 1)
    showAlert({ severity: 'success' })
  }, [closeInstanceSelectionDialog, showAlert])

  const changeUnit = useCallback((row, newUnit) => {
    if (!selectedPartId) return
    const id = row.resource.id
    const currentUnit = activeUnitByRefId[id] ?? 'available'
    if (currentUnit === newUnit) return
    const factor = row.conversionFactor
    setUnitByPartId(prev => {
      const inner = prev[selectedPartId] ?? {}
      return { ...prev, [selectedPartId]: { ...inner, [id]: newUnit } }
    })
    if (!isSecondUnitSelectable(factor)) return
    setQtyByPartId(prev => {
      const inner = prev[selectedPartId] ?? {}
      const v = inner[id] ?? 0
      if (v === 0) return prev
      const converted = toNumber(newUnit === 'secondUnit' ? v * factor : v / factor)
      const newMax = computeMaxQuantity({
        pending: row.pending,
        available: row.available,
        availableSecondUnit: row.availableSecondUnit,
        conversionFactor: factor,
        useSecondUnit: newUnit === 'secondUnit',
        generateComponents
      })
      const clamped = Math.max(0, Math.min(converted, newMax))
      const targetIntegerOnly = newUnit === 'secondUnit' && row.resource.unitary === true
      const finalValue = targetIntegerOnly
        ? Math.floor(clamped)
        : Math.round(clamped * 100) / 100
      return { ...prev, [selectedPartId]: { ...inner, [id]: finalValue } }
    })
  }, [activeUnitByRefId, selectedPartId, generateComponents])

  const handleKeypadAccept = useCallback((newVal) => {
    if (selectedPartId && keypadInfo.id) {
      let finalVal = Math.min(newVal, keypadInfo.max)
      if (!keypadInfo.fractionable) {
        finalVal = Math.floor(finalVal)
      } else if (!Number.isInteger(finalVal)) {
        finalVal = Math.round(finalVal * 100) / 100
      }
      setQtyByPartId(prev => {
        const inner = prev[selectedPartId] ?? {}
        return { ...prev, [selectedPartId]: { ...inner, [keypadInfo.id]: finalVal } }
      })
    }
  }, [keypadInfo, selectedPartId])

  // Across ALL parts, not just the active one — Confirm posts every touched part at once.
  const anyQtyToReport = useMemo(
    () => Object.values(qtyByPartId).some(inner => Object.values(inner).some(v => v > 0)),
    [qtyByPartId]
  )

  const renderScalarByUnit = useCallback((value, row) => {
    const factor = row.conversionFactor
    const selectable = row.resource.fractionable === true && isSecondUnitSelectable(factor)
    const selectedUnit = activeUnitByRefId[row.resource.id] ?? 'available'
    const displayed = selectable && selectedUnit === 'secondUnit' ? value * factor : value
    return <Typography variant='body2'>{displayed}</Typography>
  }, [activeUnitByRefId])

  const renderUnitCell = useCallback((row, cellUnit) => {
    const id = row.resource.id
    const value = cellUnit === 'secondUnit' ? (row.availableSecondUnit ?? 0) : (row.available ?? 0)
    const selectable = row.resource.fractionable === true && isSecondUnitSelectable(row.conversionFactor)
    if (!selectable) {
      return <Typography variant='body2'>{value}</Typography>
    }
    const selectedUnit = activeUnitByRefId[id] ?? 'available'
    const isSelected = selectedUnit === cellUnit
    return (
      <Box
        onClick={() => changeUnit(row, cellUnit)}
        sx={{
          cursor: 'pointer',
          display: 'inline-block',
          minWidth: 48,
          textAlign: 'center',
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          border: '2px solid',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          fontWeight: isSelected ? 'bold' : 'normal',
          '&:hover': {
            bgcolor: isSelected ? 'action.selected' : 'action.hover'
          }
        }}
      >
        <Typography variant='body2' sx={{ fontWeight: 'inherit' }}>{value}</Typography>
      </Box>
    )
  }, [activeUnitByRefId, changeUnit])

  const columns = useMemo(() => [
    {
      search: false,
      name: '',
      field: 'pending',
      readOnly: true,
      render: (row) => <CircleIcon sx={{ color: getColorForRow(row), fontSize: 18 }} />
    },
    {
      name: t_('Name'),
      field: 'resource.name',
      readOnly: true
    },
    {
      name: t_('Code'),
      field: 'resource.code',
      readOnly: true
    },
    {
      name: t_('Revision'),
      field: 'resource.revision',
      readOnly: true
    },
    {
      name: t_('Required'),
      field: 'quantityFirstUnit',
      readOnly: true,
      render: (row) => renderScalarByUnit(row.quantityFirstUnit, row)
    },
    {
      name: t_('Available'),
      field: 'available',
      readOnly: true,
      render: (row) => renderUnitCell(row, 'available')
    },
    {
      name: t_('Available (Second Unit)'),
      field: 'availableSecondUnit',
      readOnly: true,
      render: (row) => renderUnitCell(row, 'secondUnit')
    },

    {
      name: t_('Used'),
      field: 'reportedQuantity',
      readOnly: true,
      render: (row) => renderScalarByUnit(row.reportedQuantity, row)
    },
    {
      name: t_('To Use'),
      field: 'toUse',
      readOnly: true,
      render: (row) => {
        // S21/S33: SELECCION is a two-step flow for a single reference — instead of the +/-
        // quantity controls, the operator picks the reference here (step 1) and sets quantities
        // per instance in the modal (step 2, InstanceSelectionDialog). The mode is the one
        // persisted for the machine (context), not a local toggle.
        if (mode === 'SELECCION') {
          const pending = row.pending ?? 0
          return (
            <Button
              variant='outlined'
              size='small'
              disabled={pending <= 0 || !selectedPartId}
              onClick={() => openInstanceSelectionDialog(row)}
            >
              {t_('Select instances')}
            </Button>
          )
        }
        const id = row.resource.id
        const factor = row.conversionFactor
        const selectedUnit = activeUnitByRefId[id] ?? 'available'
        const useSecondUnit = selectedUnit === 'secondUnit' && isSecondUnitSelectable(factor)
        const rawMax = computeMaxQuantity({
          pending: row.pending,
          available: row.available,
          availableSecondUnit: row.availableSecondUnit,
          conversionFactor: factor,
          useSecondUnit,
          generateComponents
        })
        const allowDecimalsForCurrentUnit = allowsDecimalEntry({
          fractionable: row.resource.fractionable,
          unitary: row.resource.unitary,
          useSecondUnit,
          generateComponents,
          max: rawMax
        })
        const max = allowDecimalsForCurrentUnit ? rawMax : Math.floor(rawMax)
        const val = max > 0 ? (activeQtyByRefId[id] ?? 0) : 0
        // No part selected -> the operator cannot attribute consumption yet, so the controls are
        // inert (a part must be picked in the boxes above first).
        const controlsDisabled = !selectedPartId || max <= 0
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant='contained'
              color='inherit'
              sx={{ minWidth: 44, width: 44, height: 44, bgcolor: 'background.default' }}
              onClick={() => decQty(id)}
              disabled={val <= 0 || controlsDisabled}
            >
              <RemoveCircleOutlineIcon fontSize='large' />
            </Button>
            <Box
              sx={{
                bgcolor: 'background.default',
                px: 2,
                py: 1.25,
                borderRadius: 1,
                minWidth: 64,
                textAlign: 'center',
                cursor: controlsDisabled ? 'default' : 'pointer',
                opacity: controlsDisabled ? 0.38 : 1,
                '&:hover': {
                  bgcolor: controlsDisabled ? 'background.default' : 'action.hover'
                }
              }}
              onClick={() => !controlsDisabled && setKeypadInfo({ open: true, id, max, val, fractionable: allowDecimalsForCurrentUnit })}
            >
              <Typography variant='h6' sx={{ lineHeight: 1 }}>
                {val}
              </Typography>
            </Box>
            <Button
              variant='contained'
              color='inherit'
              sx={{ minWidth: 44, width: 44, height: 44, bgcolor: 'background.default' }}
              onClick={() => incQty(id, max)}
              disabled={val >= max || controlsDisabled}
            >
              <AddCircleOutlineIcon fontSize='large' />
            </Button>
          </Box>
        )
      }
    }
  ], [getColorForRow, t_, activeQtyByRefId, incQty, decQty, activeUnitByRefId, renderUnitCell, renderScalarByUnit, mode, openInstanceSelectionDialog, selectedPartId, generateComponents])

  const sortOptions = {
    default: {
      field: 'resource.name',
      direction: -1
    }
  }

  // Converts one (referenceId, entered quantity, chosen unit) into the RawMaterialAssemblyDto the
  // backend expects (in the "available" unit). The internal material shape is unchanged from before
  // the per-part grouping — only the envelope around it (the part) is new.
  const buildMaterial = useCallback((refId, quantity, unit) => {
    const flags = flagsByRefIdRef.current[refId] ?? {}
    const factor = flags.conversionFactor
    const useFactor = isSecondUnitSelectable(factor)
    let quantityInAvailable = (unit === 'secondUnit' && useFactor)
      ? quantity / factor
      : quantity
    if (unit === 'secondUnit' && flags.fractionable === true && flags.unitary === true && useFactor) {
      quantityInAvailable = Math.floor(quantityInAvailable * factor) / factor
    }
    // S33: the front no longer decides the consumption strategy — the server applies the mode
    // persisted for the machine, so `strategy` is never sent (regardless of the mode shown).
    return {
      rawMaterialReferenceId: Number(refId),
      quantity: Math.round(quantityInAvailable * 100) / 100,
      fractionable: flags.fractionable ?? false,
      unitary: flags.unitary ?? false
    }
  }, [])

  const handleConfirm = useCallback(async () => {
    // Group every touched part into the batch body [{ partId, materials: [...] }]. Parts with no
    // positive quantity are dropped, so a part the operator opened but left empty is not sent.
    const parts = Object.entries(qtyByPartId)
      .map(([partId, refMap]) => {
        const unitMap = unitByPartId[partId] ?? {}
        const materials = Object.entries(refMap)
          .filter(([, q]) => q > 0)
          .map(([refId, q]) => buildMaterial(refId, q, unitMap[refId] ?? 'available'))
        return { partId: Number(partId), materials }
      })
      .filter((part) => part.materials.length > 0)
    if (parts.length === 0 || !device?.id || !fabricationOrder?.id || !node?.id || loading) return
    setLoading(true)
    try {
      await apiPost('partWorkflowHistory/rawMaterial/byFoAndNodeInstance/' + device.id + '/' + fabricationOrder.id + '/' + node.id, parts)
      setQtyByPartId({})
      setUnitByPartId({})
      setRefreshKey(v => v + 1)
      showAlert({ severity: 'success' })
    } catch (error) {
      console.error('Error confirming resources:', error)
      showAlert({ severity: 'error', message: error?.message })
    } finally {
      setLoading(false)
    }
  }, [qtyByPartId, unitByPartId, buildMaterial, device?.id, fabricationOrder?.id, node?.id, loading, showAlert])

  if (!preview && !jobReference) {
    return <DefaultBeforeSelectText />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      {AlertElement}
      <ResourceReportDialog
        open={dialogInfo.open}
        item={dialogInfo.item}
        setOpen={(open) => setDialogInfo({ ...dialogInfo, open })}
        deviceId={device?.id}
        fabricationOrderId={fabricationOrder?.id}
        nodeId={node?.id}
      />
      <InstanceSelectionDialog
        open={instanceSelectionDialog.open}
        deviceId={device?.id}
        referenceId={instanceSelectionDialog.referenceId}
        referenceCode={instanceSelectionDialog.referenceCode}
        fabricationOrderId={fabricationOrder?.id}
        nodeId={node?.id}
        partId={selectedPartId}
        requiredQuantity={instanceSelectionDialog.pending}
        selectableQuantity={instanceSelectionDialog.available}
        generateComponents={generateComponents}
        onClose={closeInstanceSelectionDialog}
        onSuccess={handleInstanceSelectionSuccess}
      />
      <NumericKeypad
        open={keypadInfo.open}
        onClose={() => setKeypadInfo(prev => ({ ...prev, open: false }))}
        onAccept={handleKeypadAccept}
        initialValue={keypadInfo.val}
        title={t_('Report Quantity')}
        allowDecimals={keypadInfo.fractionable}
      />
      <PartSelectorBar
        parts={reportableParts}
        selectedPartId={selectedPartId}
        onSelect={setSelectedPartId}
        partsWithQty={partsWithQty}
        t_={t_}
      />
      {progress.total > 0 && (
        <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography
              variant='caption'
              sx={{ color: progress.allDone ? 'success.main' : 'warning.main', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
            >
              {progress.allDone ? t_('All completed') : `${progress.pending} ${t_('pending')}`}
            </Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={progress.percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: progress.allDone ? 'success.main' : 'warning.main',
                borderRadius: 3
              }
            }}
          />
        </Box>
      )}
      <Box sx={{ overflow: 'auto', width: '100%', flexGrow: 1 }}>
        <MosSmartTable
          columns={columns}
          dataFetch={dataFetchWithProgress}
          forceLoad={selectedPartId + '-' + refreshKey}
          sortOptions={sortOptions}
          showSearchHeader
          toolbarDisplay='flex'
          searchAndPaginationVisible
          hideExport
          compactHeader
          searchPosition='left'
          toolBarStyle={{
            height: 'auto',
            minHeight: 48,
            mb: 0.5,
            px: 1,
            '& .MuiToolbar-root': {
              minHeight: 48,
              padding: '0 8px'
            }
          }}
          toolbar={{
            renderSticky: {
              fetchFunction: () => (
                <Button
                  variant='contained'
                  color='success'
                  disabled={!anyQtyToReport || loading}
                  onClick={handleConfirm}
                  size='medium'
                  sx={{
                    ml: 1,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: 1.5,
                    boxShadow: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '1rem',
                    minHeight: 40,
                    '&:hover': {
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading ? t_('Processing...') : t_('Confirm')}
                </Button>
              )
            }
          }}
        />
      </Box>
    </Box>
  )
}

class ResourceListWidgetProvider extends BaseWidgetProvider {
  getCode = () => 'ResourceListWidget'

  getName = () => 'Components'

  getIcon = () => GridViewIcon

  createWidget = (props) => <ResourceListWidget {...props} />

  createPreviewWidget = (props) => <ResourceListWidget {...props} preview />

  size = { defaultW: 4, defaultH: 4, minW: 4, minH: 4 }

  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size) }
  }
}

export default ResourceListWidgetProvider
